import React, { useState } from "react";
import moment from "moment";
import { CheckedInUser } from "src/components/CheckedIn/checkedInUser";
import { CheckedInAction } from "src/components/CheckedIn/CheckedInSiteStyles";
import { PostCard, PostContent, ViewerMessage } from "src/components/CheckedIn/CheckedInContentStyles";
import { StandardModal } from "src/components";
import { CLIENT_NOTICEBOARD } from "src/constants/permissions";
import NoticeboardFeedMedia from "../components/feed/NoticeboardFeedMedia";
import NoticeboardFeedAttachments from "../components/feed/NoticeboardFeedAttachments";
import NoticeboardFeedCommentPreview from "../components/feed/NoticeboardFeedComments";
import { CheckInDisplay } from "../components/CheckInSection";
import {
    getNoticeCommentCount, getNoticeInteractionUuid, getNoticePublication,
    incrementNoticeCommentCount, setNoticePublicationPinned
} from "../feedUtils";
import { deleteNotice, pinNoticePublication, unpinNoticePublication } from "../api/feedApi";
import { Notice, NoticePublication } from "./noticeTypes";
import { NoticeReactions } from "./NoticeReactions";
import { NoticeDiscussion, NoticeFooter, NoticeMediaWrap, NoticeTextButton } from "./NoticeViewerStyles";

interface NoticePostProps {
    notice: Notice;
    user: CheckedInUser;
    detail: boolean;
    onOpen: () => void;
    onUpdated: (notice: Notice) => void;
    onDeleted: (uuid: string) => void;
}

export function NoticePost({ notice, user, detail, onOpen, onUpdated, onDeleted }: NoticePostProps): React.ReactElement {
    const [commentsOpen, setCommentsOpen] = useState<boolean>(false);
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<boolean>(false);
    const [pinBusy, setPinBusy] = useState<boolean>(false);
    const [pinError, setPinError] = useState<boolean>(false);
    const interactionUuid: string = getNoticeInteractionUuid(notice, null);
    const commentCount: number = getNoticeCommentCount(notice, null);
    const publication: NoticePublication | null = getNoticePublication(notice, null);
    const canPin: boolean = Boolean(publication) && user.permissions.includes(CLIENT_NOTICEBOARD);
    const ownPost: boolean = Boolean(user.employeeUuid) && notice.employeeUuid === user.employeeUuid;
    const message: string = notice.message || "";
    const truncated: boolean = !detail && message.length > 200;
    const remove = async (): Promise<void> => {
        if (!ownPost || deleting) {
            return;
        }
        setDeleting(true);
        setDeleteError(false);
        try {
            await deleteNotice(user.token, notice.uuid);
            onDeleted(notice.uuid);
        } catch {
            setDeleteError(true);
        } finally {
            setDeleting(false);
        }
    };
    const togglePin = async (): Promise<void> => {
        if (!canPin || !publication || pinBusy) {
            return;
        }
        setPinBusy(true);
        setPinError(false);
        try {
            if (publication.isPinned) {
                await unpinNoticePublication(user.token, publication.uuid);
            } else {
                await pinNoticePublication(user.token, publication.uuid);
            }
            onUpdated(setNoticePublicationPinned(notice, publication.uuid, !publication.isPinned));
        } catch {
            setPinError(true);
        } finally {
            setPinBusy(false);
        }
    };
    return <PostCard $detail={detail} aria-label={notice.subject || "Noticeboard post"}>
        <NoticeMediaWrap>
            <NoticeboardFeedMedia notice={notice} onOpenPost={onOpen} modalMode={detail} initialIndex={0}
                hideVideoPostButton />
        </NoticeMediaWrap>
        <NoticeReactions notice={notice} token={user.token} interactionUuid={interactionUuid} onUpdated={onUpdated} />
        <PostContent>
            <p>Posted by {notice.postedBy || "Unavailable user"}</p>
            <h2>{detail ? notice.subject : <NoticeTextButton type="button" onClick={onOpen}>
                {notice.subject || "View post"}</NoticeTextButton>}</h2>
            {message && <p>{truncated ? `${message.slice(0, 200).trimEnd()}…` : message}</p>}
            {truncated && <NoticeTextButton type="button" onClick={onOpen}>Show More</NoticeTextButton>}
            {detail && <p><strong>Posted: </strong><time dateTime={notice.postedDate}>
                {moment.utc(notice.postedDate).local().format("Do MMMM, YYYY")}</time></p>}
        </PostContent>
        <NoticeboardFeedAttachments attachments={notice.attachments} />
        <CheckInDisplay checkIn={notice.checkIn} detail={detail} dashboard={false} />
        {(notice.allowComments || commentCount > 0) && <NoticeDiscussion aria-label="Comments">
            {detail ? <h3>Comments · {commentCount}</h3> : <NoticeTextButton type="button" aria-expanded={commentsOpen}
                onClick={() => setCommentsOpen(value => !value)}>
                {commentCount} {commentCount === 1 ? "comment" : "comments"} · Comment
            </NoticeTextButton>}
            {(detail || commentsOpen) && <NoticeboardFeedCommentPreview token={user.token} noticeUuid={notice.uuid}
                interactionUuid={interactionUuid} commentCount={commentCount} allowComments={notice.allowComments}
                showComposer onCommentCreated={() => onUpdated(incrementNoticeCommentCount(notice, interactionUuid))} />}
        </NoticeDiscussion>}
        {!notice.allowComments && detail && <ViewerMessage>Comments are turned off for this post.</ViewerMessage>}
        {detail && (canPin || ownPost) && <NoticeFooter>
            {canPin && <CheckedInAction type="button" $outline disabled={pinBusy} aria-pressed={publication?.isPinned}
                onClick={() => void togglePin()}>{pinBusy ? "Saving…" : publication?.isPinned ? "Unpin Post" : "Pin Post"}</CheckedInAction>}
            {ownPost && <CheckedInAction type="button" $monochrome onClick={() => setConfirmDelete(true)}>Delete Post</CheckedInAction>}
            {pinError && <ViewerMessage $error role="alert">The pinned status could not be changed. Please try again.</ViewerMessage>}
        </NoticeFooter>}
        {ownPost && <StandardModal open={confirmDelete} onClose={() => { if (!deleting) { setConfirmDelete(false); } }}
            title="Delete post?" description="This will permanently delete your post." widthMode="small">
            <NoticeFooter>
                <CheckedInAction type="button" $outline disabled={deleting} onClick={() => setConfirmDelete(false)}>Cancel</CheckedInAction>
                <CheckedInAction type="button" $monochrome disabled={deleting} onClick={() => void remove()}>
                    {deleting ? "Deleting…" : "Delete Post"}</CheckedInAction>
            </NoticeFooter>
            {deleteError && <ViewerMessage $error role="alert">Your post could not be deleted. Please try again.</ViewerMessage>}
        </StandardModal>}
    </PostCard>;
}
